import { useState } from "react";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { NumInput } from "@/components/NumInput";
import { ProductImage } from "@/components/ProductImage";
import { api, brl, errMsg, pct, channelPct } from "@/lib/api";

const Cell = ({ r, v, onSaved }) => {
  const [edit, setEdit] = useState(null);
  const save = async () => {
    const val = Number(edit) || 0;
    setEdit(null);
    if (Math.abs(val - ((v.own || v.is_default) ? v.price : 0)) < 0.005) return;
    try { await api.patch(`/recipes/${r.id}/price`, { sale_price: val, channel_id: v.channel_id }); toast.success(`${r.name} · ${v.channel_name}: ${val > 0 ? brl(val) : "volta a herdar o padrão"}`); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (edit != null) {
    return <td className="p-1"><NumInput autoFocus value={edit} onChange={(e) => setEdit(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEdit(null); }} className="num h-8 w-24 text-right" data-testid={`matrix-input-${r.id}-${v.channel_id}`} /></td>;
  }
  const inherited = !v.own && !v.is_default;
  return (
    <td className="p-1">
      <button type="button" onClick={() => setEdit(v.price ? v.price.toFixed(2) : "")} title={inherited ? "Herda o preço padrão — clique para definir um preço próprio" : "Clique para editar"}
        className={`w-full rounded-lg px-2 py-1.5 text-right hover:bg-secondary transition-colors ${inherited ? "text-muted-foreground" : "font-semibold"}`} data-testid={`matrix-cell-${r.id}-${v.channel_id}`}>
        <span className="num block">{v.price ? brl(v.price) : "—"}{inherited && v.price ? <span className="text-[9px] align-top ml-0.5">*</span> : ""}</span>
        <span className={`num text-[11px] block ${v.contribution_margin != null && v.contribution_margin < 0 ? "text-rose-500" : "text-muted-foreground"}`}>{pct(v.contribution_margin_pct)}</span>
      </button>
    </td>
  );
};

// Tabela geral: todos os produtos × todos os canais. Clique na célula para editar; Enter/clicar fora salva; vazio = volta a herdar.
export const PriceMatrix = ({ recipes, channels, reload }) => {
  const rows = [...(recipes || [])].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="rounded-2xl border border-border bg-card rise" data-testid="price-matrix">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border text-xs">
        <p className="text-muted-foreground">Preço de venda e margem líquida real por canal · clique na célula para editar</p>
        <p className="text-muted-foreground">* herda o preço padrão</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase text-muted-foreground border-b border-border">
              <th className="text-left p-2 sm:p-3 sticky left-0 bg-card z-10 min-w-[180px] sm:min-w-[230px]">Produto</th>
              {(channels || []).map((c) => <th key={c.id} className="text-right p-2 min-w-[112px] whitespace-nowrap"><span className="flex items-center justify-end gap-1">{c.is_default && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}{c.name}</span><span className="num font-normal normal-case">{pct(channelPct(c), 2)}</span></th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/50" data-testid={`matrix-row-${r.id}`}>
                <td className="p-2 sm:p-3 sticky left-0 bg-card z-10">
                  <div className="flex items-center gap-2 min-w-0">
                    <ProductImage recipe={r} size="sm" testId={`matrix-image-${r.id}`} />
                    <div className="min-w-0"><p className="font-medium truncate max-w-[120px] sm:max-w-[170px]">{r.name}</p><p className="text-[11px] text-muted-foreground num">custo {brl(r.cost_per_portion)}</p></div>
                  </div>
                </td>
                {(r.prices_by_channel || []).map((v) => <Cell key={v.channel_id} r={r} v={v} onSaved={reload} />)}
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <p className="p-6 text-sm text-muted-foreground text-center">Nenhuma ficha técnica cadastrada.</p>}
      </div>
    </div>
  );
};
